import { Box, Button, MenuItem, Typography } from '@mui/material'
import { type GridRenderCellParams } from '@mui/x-data-grid'
import { StyledMenu } from 'components/StyledMenu/StyledMenu'
import ListBullet from 'assets/format_list_bulleted.svg'
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { type SweepstakeWinner } from 'src/types/api'
import { usePutSweepstakeWinner } from 'hooks/api/usePutSweepstakeWinner'
import { usePutSweepstakeIfAllWinnersAwarded } from 'hooks/api/usePutSweepstakeIfAllWinnersAwarded'
import { LocationSweepstakesRedrawModal } from './LocationSweepstakesRedrawModal'
import { LocationSweepstakesNotifyWinnersModal } from './LocationSweepstakesNotifyWinnersModal'

interface RedrawWinnerMenuItemProps {
  handleClose: () => void
  setIsRedrawModalOpen: () => void
}

export const RedrawWinnerMenuItem = ({
  handleClose,
  setIsRedrawModalOpen,
}: RedrawWinnerMenuItemProps) => (
  <MenuItem
    onClick={() => {
      handleClose()
      setIsRedrawModalOpen()
    }}
  >
    <Box className="py-2">
      <Typography variant="body-2">Redraw Winner</Typography>
    </Box>
  </MenuItem>
)

export interface LocationWinnerTableProps {
  id: number
  leName: string
  leLicenseNumber: string
  winnerName: string
  patronId: number
  winnerStatus: string
  dto: SweepstakeWinner
  resultType: string | undefined
  drawingSequence: number | undefined
  drawnDate: string
  prize: string
  isCashPrize: boolean
}

export const LocationPrizeMenu = ({
  params,
  sweepstakeName,
}: {
  params: GridRenderCellParams<LocationWinnerTableProps>
  sweepstakeName: string
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedSweepstakeWinnerId, setSelectedSweepstakeWinnerId] = useState<
    number | undefined
  >()
  const qc = useQueryClient()
  const [isRedrawModalOpen, setIsRedrawModalOpen] = useState(false)
  const [isNotifyWinnersModalOpen, setIsNotifyWinnersModalOpen] =
    useState(false)
  const toggleNotifyWinnersModalOpen = () =>
    setIsNotifyWinnersModalOpen(!isNotifyWinnersModalOpen)

  const toggleMenu = (event: React.MouseEvent<HTMLElement>, id: number) => {
    event.stopPropagation()
    if (anchorEl != null) {
      setAnchorEl(null)
      setSelectedSweepstakeWinnerId(undefined)
    } else {
      setSelectedSweepstakeWinnerId(id)
      setAnchorEl(event.currentTarget)
    }
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const onRedrawModalOpen = () => setIsRedrawModalOpen(true)
  const onRedrawCancel = () => setIsRedrawModalOpen(false)

  const open = anchorEl !== null

  const status = params.row.winnerStatus

  const { mutateAsync: mutateSweepstakeAsync } =
    usePutSweepstakeIfAllWinnersAwarded({
      sweepstakeId: Number(params.row.dto.contestEntry?.contestId),
      onError: () => {},
      onSuccess: () => {},
    })

  const { mutateAsync } = usePutSweepstakeWinner({
    onError: () => {},
    onSuccess: (data) => {
      void qc.invalidateQueries({
        queryKey: [
          '/sweepstakes/drawing-info/multi-draw',
          Number(params.row.dto.contestEntry?.contestId),
        ],
        exact: true,
      })
      setAnchorEl(null)
      // If winner has been awarded, check if all winners are awarded and mark contest as "Closed"
      if (data.status === 'Awarded') {
        void mutateSweepstakeAsync()
      }
    },
  })

  if (status === 'NoWinner' || status === 'Awarded') {
    return null
  }

  return (
    <>
      <Button
        onClick={(e) => {
          e.stopPropagation()
          toggleMenu(e, params.row.id)
        }}
        style={{
          minWidth: '50px',
        }}
      >
        <img src={ListBullet} width={4} height={16} alt="options icon" />
      </Button>
      <StyledMenu
        sx={{ mt: 0 }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {[
          (status === 'WinnerValidation' ||
            status === 'LocationValidation') && (
            <MenuItem
              key="notifyWinner"
              onClick={() => {
                handleClose()
                setIsNotifyWinnersModalOpen(true)
              }}
            >
              <Box className="py-2">
                <Typography variant="body-2">Notify Winner</Typography>
              </Box>
            </MenuItem>
          ),
          // if status is WinnerNotified and a non cash prize, allow awarding the winner
          status === 'WinnerNotified' && !params.row.isCashPrize && (
            <MenuItem
              key="awardWinner"
              onClick={async () =>
                await mutateAsync({
                  id: Number(selectedSweepstakeWinnerId),
                  status: 'Awarded',
                  sweepstakeDrawingId: params.row.dto.sweepstakeDrawingId,
                  contestEntryId: params.row.dto.contestEntryId,
                  patronId: params.row.patronId,
                  dateDrawn: params.row.drawnDate,
                  notes: params.row.dto.notes ?? null,
                  drawingSequence: params.row.drawingSequence ?? 0,
                  redrawReason: params.row.dto.redrawReason ?? '',
                  redrawCount: Number(params.row.dto.redrawCount) + 1,
                  locationValidationStartDate:
                    params.row.dto.locationValidationStartDate ?? null,
                  contestWinnerId: params.row.dto.contestWinnerId ?? null,
                })
              }
            >
              <Box className="py-2">
                <Typography variant="body-2">Award Winner</Typography>
              </Box>
            </MenuItem>
          ),
          // if status is AffidavitReceived, allow awarding the winner
          status === 'AffidavitReceived' && (
            <MenuItem
              key="awardWinner"
              onClick={async () =>
                await mutateAsync({
                  id: Number(selectedSweepstakeWinnerId),
                  status: 'Awarded',
                  sweepstakeDrawingId: params.row.dto.sweepstakeDrawingId,
                  contestEntryId: params.row.dto.contestEntryId,
                  patronId: params.row.patronId,
                  dateDrawn: params.row.drawnDate,
                  notes: params.row.dto.notes ?? null,
                  drawingSequence: params.row.drawingSequence ?? 0,
                  redrawReason: params.row.dto.redrawReason ?? '',
                  redrawCount: Number(params.row.dto.redrawCount) + 1,
                  locationValidationStartDate:
                    params.row.dto.locationValidationStartDate ?? null,
                  contestWinnerId: params.row.dto.contestWinnerId ?? null,
                })
              }
            >
              <Box className="py-2">
                <Typography variant="body-2">Award Winner</Typography>
              </Box>
            </MenuItem>
          ),
          status !== 'Awarded' && status !== 'AffidavitReceived' && (
            <RedrawWinnerMenuItem
              key="redrawWinner"
              handleClose={handleClose}
              setIsRedrawModalOpen={onRedrawModalOpen}
            />
          ),
        ].filter(Boolean)}
      </StyledMenu>
      <LocationSweepstakesRedrawModal
        isModalOpen={isRedrawModalOpen}
        onCancel={onRedrawCancel}
        winnerName={params.row.winnerName}
        sweepstakeDrawingId={params.row.dto.sweepstakeDrawingId}
        sweepstakeWinnerId={params.row.dto.id}
        redrawCount={params.row.dto.redrawCount ?? 0}
      />
      <LocationSweepstakesNotifyWinnersModal
        isModalOpen={isNotifyWinnersModalOpen}
        toggleIsOpen={toggleNotifyWinnersModalOpen}
        sweepstakeName={sweepstakeName}
        selectedWinners={[
          {
            locationName: params.row.leName,
            licenseNumber: params.row.leLicenseNumber,
            winnerName: params.row.winnerName,
            sweepstakesWinner: params.row.dto,
            drawingSequence: params.row.drawingSequence ?? 0,
          },
        ]}
      />
    </>
  )
}
